import {
  CalculatorAdditionalPaymentApiRequest,
  CalculatorAdditionalPaymentStep1FormFields,
  CalculatorAdditionalPaymentStep2FormFields,
  CalculatorAdditionalPaymentFormFields
} from '../types';
import { IntlShape } from 'react-intl/src/types';
import {
  useAdditionalPaymentStep1FormFields,
  useAdditionalPaymentStep2FormFields
} from '../config';
import { differenceInYears, differenceInMonths } from 'date-fns';
import { percentageToFloat } from 'src/utils';

const utilsAdditionalPaymentGetDefaultValuesStep1 = (
  intl: IntlShape
): CalculatorAdditionalPaymentStep1FormFields => {
  const calculatorBuyerCostsStep1FormFields = useAdditionalPaymentStep1FormFields(intl);
  return {
    mortgageType: calculatorBuyerCostsStep1FormFields.mortgageType.defaultValue,
    remainingTerm: calculatorBuyerCostsStep1FormFields.remainingTerm.defaultValue,
    mortgageAmount: calculatorBuyerCostsStep1FormFields.mortgageAmount.defaultValue,
    mortgageInterestRate: calculatorBuyerCostsStep1FormFields.mortgageInterestRate.defaultValue,
    mortgageAmount2: calculatorBuyerCostsStep1FormFields.mortgageAmount2.defaultValue,
    mortgageInterestRate2: calculatorBuyerCostsStep1FormFields.mortgageInterestRate2.defaultValue
  };
};

const utilsAdditionalPaymentGetDefaultValuesStep2 = (
  intl: IntlShape
): CalculatorAdditionalPaymentStep2FormFields => {
  const calculatorBuyerCostsStep2FormFields = useAdditionalPaymentStep2FormFields(intl);
  return {
    additionalPayment: calculatorBuyerCostsStep2FormFields.additionalPayment.defaultValue,
    paymentType: calculatorBuyerCostsStep2FormFields.paymentType.defaultValue
  };
};

const utilsAdditionalPaymentGetTranslationFormOptions = (
  intl: IntlShape,
  key: 'mortgageType' | 'paymentType',
  value: string
): string => {
  const calculatorBuyerCostsFormFields = {
    ...useAdditionalPaymentStep1FormFields(intl),
    ...useAdditionalPaymentStep2FormFields(intl)
  };

  const translatedLabel = calculatorBuyerCostsFormFields[key].options?.find(
    (option) => option.value === value
  );

  return translatedLabel ? translatedLabel.label : '';
};

const utilsAdditionalPaymentPrepareFormData = (
  formData: Record<keyof CalculatorAdditionalPaymentFormFields, string>
): CalculatorAdditionalPaymentApiRequest => {
  const {
    mortgageType,
    remainingTerm,
    mortgageAmount,
    mortgageInterestRate,
    mortgageAmount2,
    mortgageInterestRate2,
    additionalPayment,
    paymentType
  } = formData;

  const date = new Date(remainingTerm);
  const remainingMonths = differenceInMonths(date, new Date());
  const postData: CalculatorAdditionalPaymentApiRequest = {
    mortgageType,
    remainingTerm: Number(remainingMonths),
    mortgageAmount: Number(mortgageAmount),
    mortgageInterestRate: percentageToFloat(mortgageInterestRate),
    additionalPayment: Number(additionalPayment),
    additionalPaymentOnMortgageType: paymentType ? paymentType : 'hfd'
  };

  if (mortgageType === 'annafl' || mortgageType === 'linafl') {
    postData.mortgageAmount2 = Number(mortgageAmount2);
    postData.mortgageInterestRate = mortgageInterestRate2
      ? percentageToFloat(mortgageInterestRate2)
      : 0;
  }
  return postData;
};

export const utilsCalculateRemainingTerm = (
  futureDateString: string
): { years: number; months: number } => {
  const futureDate = new Date(futureDateString);
  const now = new Date();
  const years = differenceInYears(futureDate, now);
  const months = differenceInMonths(futureDate, now) % 12;
  return { years, months };
};

export {
  utilsAdditionalPaymentGetDefaultValuesStep1,
  utilsAdditionalPaymentGetDefaultValuesStep2,
  utilsAdditionalPaymentGetTranslationFormOptions,
  utilsAdditionalPaymentPrepareFormData
};

export const utilsGetChartOptions = (
  ticksArray: Date[]
): google.visualization.LineChartOptions => ({
  fontName: 'DINNextLTProLight',
  theme: 'material',
  fontSize: 16,
  interpolateNulls: true,
  curveType: 'function',
  legend: {
    position: 'top',
    alignment: 'end',
    textStyle: { color: '#000000', fontSize: 16 }
  },
  hAxis: {
    textStyle: { color: '#000000', fontSize: 14 },
    gridlines: { color: 'transparent' },
    format: 'yyyy',
    ticks: ticksArray,
    viewWindow: {
      min: new Date(ticksArray[0].getFullYear(), ticksArray[0].getMonth() - 2), // Subtract 6 months

      max: ticksArray[ticksArray.length - 1] // Use the last tick as the maximum
    }
  },
  vAxis: {
    format: 'short',
    textStyle: { color: '#000000', fontSize: 14 },
    gridlines: { color: 'transparent' },
    baselineColor: 'transparent'
  },
  series: {
    0: { color: '#C61118' },
    1: { color: '#00325D' }
  },
  chartArea: {
    left: 50,
    top: 20,
    right: 30
  },
  pointSize: 5,
  backgroundColor: {
    fill: 'transparent'
  },
  tooltip: { textStyle: { color: '#000000' }, showColorCode: true }
});
